import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageSection } from "@/components/AppLayout.jsx";
import { GsdReadableBlock } from "@/coquille-bridge";
import { getDataStore } from "@/data/dataStore.js";
import { useMembershipCopy, useMembershipStore } from "@/membership/useMembership.js";
import {
  chargeMembershipSandbox,
  createMembershipDraft,
  getMembershipById,
  updateConsents,
  updateHealth,
  updateIdentity,
} from "@/membership/membershipStore.js";
import { getHealthFields } from "@/membership/healthFormCatalog.js";
import { emptyHealthAnswers } from "@/membership/healthFormCatalog.js";
import { PAYMENT_METHODS, REGISTRATION_KIND, SANDBOX_MEMBER_PASSWORD } from "@/membership/membershipConstants.js";
import { formatFee } from "@/membership/membershipI18n.js";
import { nextMissingStep } from "@/membership/membershipEngine.js";

const STEPS = ["kind", "identity", "health", "consents", "payment", "done"];

function Field({ id, label, children }) {
  return (
    <label htmlFor={id}>
      {label}
      {children}
    </label>
  );
}

export function BecomeMemberWizardPage() {
  const { locale, copy } = useMembershipCopy();
  const store = useMembershipStore();
  const dojos = getDataStore().dojos || [];
  const [step, setStep] = useState("kind");
  const [membershipId, setMembershipId] = useState(null);
  const [error, setError] = useState("");
  const [identity, setIdentity] = useState({
    firstName: "",
    lastName: "",
    birthDate: "",
    address: "",
    city: "",
    province: "Québec",
    postalCode: "",
    phone: "",
    email: "",
    dojoId: dojos[0]?.id || "",
    instructorId: dojos[0]?.sensei_id || "",
  });
  const [emergency, setEmergency] = useState({ name: "", relation: "", phone: "" });
  const [parent, setParent] = useState({
    firstName: "",
    lastName: "",
    relation: "Parent",
    phone: "",
    email: "",
  });
  const [health, setHealth] = useState(emptyHealthAnswers());
  const [consents, setConsents] = useState({
    privacy: false,
    healthAccuracy: false,
    photoOptional: false,
    parentalAuthority: false,
  });
  const [payMethod, setPayMethod] = useState(PAYMENT_METHODS.SANDBOX);
  const [kind, setKind] = useState(null);

  const record = membershipId ? getMembershipById(membershipId) : null;
  const w = copy.wizard;
  const f = copy.fields;
  const fee = formatFee(store.settings.annualFeeCents, store.settings.currency, locale);

  const healthFields = useMemo(() => getHealthFields(kind || REGISTRATION_KIND.ADULT), [kind]);

  function ensureDraft(nextKind) {
    if (membershipId) return membershipId;
    const draft = createMembershipDraft({ kind: nextKind, origin: "user", demo: true });
    setMembershipId(draft.id);
    return draft.id;
  }

  function onChooseKind(nextKind) {
    setKind(nextKind);
    ensureDraft(nextKind);
    setStep("identity");
  }

  function onSaveIdentity() {
    setError("");
    const id = ensureDraft(kind);
    const saved = updateIdentity(id, identity, {
      emergency,
      parent: kind === REGISTRATION_KIND.MINOR ? parent : null,
    });
    if (nextMissingStep(saved) === "identity") {
      setError(w.missing);
      return;
    }
    setStep("health");
  }

  function onSaveHealth() {
    setError("");
    const saved = updateHealth(membershipId, health, identity.email || parent.email);
    if (nextMissingStep(saved) === "health") {
      setError(w.missing);
      return;
    }
    setStep("consents");
  }

  function onSaveConsents() {
    setError("");
    const patch = { ...consents };
    if (kind !== REGISTRATION_KIND.MINOR) patch.parentalAuthority = true;
    const saved = updateConsents(membershipId, patch);
    if (nextMissingStep(saved) === "consents") {
      setError(w.missing);
      return;
    }
    setStep("payment");
  }

  async function onPay() {
    setError("");
    const payer = kind === REGISTRATION_KIND.MINOR ? parent.email : identity.email;
    const result = await chargeMembershipSandbox(membershipId, payMethod, payer);
    if (!result.ok) {
      setError(w.missing);
      return;
    }
    setStep("done");
  }

  const loginId = kind === REGISTRATION_KIND.MINOR ? parent.email : identity.email;

  return (
    <PageSection title={w.title} subtitle={w.subtitle} testId="page-devenir-membre">
      <GsdReadableBlock className="membership-page membership-wizard" data-surface="light" testId="membership-wizard">
        <ol className="membership-stepper" data-testid="membership-stepper">
          {STEPS.filter((s) => s !== "done" || step === "done").map((s) => (
            <li key={s} className={step === s ? "is-active" : ""}>
              {s === "kind" ? w.stepKind : s === "identity" ? w.stepIdentity : s === "health" ? w.stepHealth : s === "consents" ? w.stepConsents : s === "payment" ? w.stepPayment : w.stepDone}
            </li>
          ))}
        </ol>
        {error ? (
          <p className="membership-notice" data-testid="membership-error">
            {error}
          </p>
        ) : null}

        {step === "kind" ? (
          <div data-testid="membership-step-kind">
            <h2>{w.stepKind}</h2>
            <div className="membership-kind-grid">
              <button type="button" className="membership-kind-btn" data-testid="membership-kind-adult" onClick={() => onChooseKind(REGISTRATION_KIND.ADULT)}>
                {w.adult}
              </button>
              <button type="button" className="membership-kind-btn" data-testid="membership-kind-minor" onClick={() => onChooseKind(REGISTRATION_KIND.MINOR)}>
                {w.minor}
              </button>
            </div>
          </div>
        ) : null}

        {step === "identity" ? (
          <form
            className="membership-form"
            data-testid="membership-step-identity"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              onSaveIdentity();
            }}
          >
            <h2>{w.stepIdentity} — {kind === "minor" ? w.minor : w.adult}</h2>
            <Field id="ms-first" label={f.firstName}>
              <input id="ms-first" data-testid="membership-field-firstName" value={identity.firstName} onChange={(e) => setIdentity({ ...identity, firstName: e.target.value })} required />
            </Field>
            <Field id="ms-last" label={f.lastName}>
              <input id="ms-last" data-testid="membership-field-lastName" value={identity.lastName} onChange={(e) => setIdentity({ ...identity, lastName: e.target.value })} required />
            </Field>
            <Field id="ms-birth" label={f.birthDate}>
              <input id="ms-birth" type="date" data-testid="membership-field-birthDate" value={identity.birthDate} onChange={(e) => setIdentity({ ...identity, birthDate: e.target.value })} required />
            </Field>
            <Field id="ms-addr" label={f.address}>
              <input id="ms-addr" data-testid="membership-field-address" value={identity.address} onChange={(e) => setIdentity({ ...identity, address: e.target.value })} required />
            </Field>
            <Field id="ms-city" label={f.city}>
              <input id="ms-city" data-testid="membership-field-city" value={identity.city} onChange={(e) => setIdentity({ ...identity, city: e.target.value })} required />
            </Field>
            <Field id="ms-prov" label={f.province}>
              <input id="ms-prov" data-testid="membership-field-province" value={identity.province} onChange={(e) => setIdentity({ ...identity, province: e.target.value })} required />
            </Field>
            <Field id="ms-postal" label={f.postalCode}>
              <input id="ms-postal" data-testid="membership-field-postalCode" value={identity.postalCode} onChange={(e) => setIdentity({ ...identity, postalCode: e.target.value })} required />
            </Field>
            <Field id="ms-phone" label={f.phone}>
              <input id="ms-phone" data-testid="membership-field-phone" value={identity.phone} onChange={(e) => setIdentity({ ...identity, phone: e.target.value })} required />
            </Field>
            {kind === REGISTRATION_KIND.ADULT ? (
              <Field id="ms-email" label={f.email}>
                <input id="ms-email" type="email" data-testid="membership-field-email" value={identity.email} onChange={(e) => setIdentity({ ...identity, email: e.target.value })} required />
              </Field>
            ) : (
              <Field id="ms-child-email" label={f.email}>
                <input id="ms-child-email" data-testid="membership-field-email" value={identity.email} onChange={(e) => setIdentity({ ...identity, email: e.target.value })} />
              </Field>
            )}
            <Field id="ms-dojo" label={f.dojo}>
              <select
                id="ms-dojo"
                data-testid="membership-field-dojo"
                value={identity.dojoId}
                onChange={(e) => {
                  const dojo = dojos.find((d) => d.id === e.target.value);
                  setIdentity({ ...identity, dojoId: e.target.value, instructorId: dojo?.sensei_id || "" });
                }}
                required
              >
                {dojos.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.nom}
                  </option>
                ))}
              </select>
            </Field>
            {kind === REGISTRATION_KIND.MINOR ? (
              <>
                <h3>{copy.wizard.parentManages}</h3>
                <Field id="ms-p-first" label={f.parentFirstName}>
                  <input id="ms-p-first" data-testid="membership-field-parentFirstName" value={parent.firstName} onChange={(e) => setParent({ ...parent, firstName: e.target.value })} required />
                </Field>
                <Field id="ms-p-last" label={f.parentLastName}>
                  <input id="ms-p-last" data-testid="membership-field-parentLastName" value={parent.lastName} onChange={(e) => setParent({ ...parent, lastName: e.target.value })} required />
                </Field>
                <Field id="ms-p-rel" label={f.parentRelation}>
                  <input id="ms-p-rel" data-testid="membership-field-parentRelation" value={parent.relation} onChange={(e) => setParent({ ...parent, relation: e.target.value })} required />
                </Field>
                <Field id="ms-p-phone" label={f.parentPhone}>
                  <input id="ms-p-phone" data-testid="membership-field-parentPhone" value={parent.phone} onChange={(e) => setParent({ ...parent, phone: e.target.value })} required />
                </Field>
                <Field id="ms-p-email" label={f.parentEmail}>
                  <input id="ms-p-email" type="email" data-testid="membership-field-parentEmail" value={parent.email} onChange={(e) => setParent({ ...parent, email: e.target.value })} required />
                </Field>
              </>
            ) : null}
            <Field id="ms-em-name" label={f.emergencyName}>
              <input id="ms-em-name" data-testid="membership-field-emergencyName" value={emergency.name} onChange={(e) => setEmergency({ ...emergency, name: e.target.value })} required />
            </Field>
            <Field id="ms-em-rel" label={f.emergencyRelation}>
              <input id="ms-em-rel" data-testid="membership-field-emergencyRelation" value={emergency.relation} onChange={(e) => setEmergency({ ...emergency, relation: e.target.value })} required />
            </Field>
            <Field id="ms-em-phone" label={f.emergencyPhone}>
              <input id="ms-em-phone" data-testid="membership-field-emergencyPhone" value={emergency.phone} onChange={(e) => setEmergency({ ...emergency, phone: e.target.value })} required />
            </Field>
            <div className="membership-actions">
              <button type="button" className="btn-secondary" onClick={() => setStep("kind")}>
                {w.back}
              </button>
              <button type="submit" className="btn-primary" data-testid="membership-identity-next">
                {w.next}
              </button>
            </div>
          </form>
        ) : null}

        {step === "health" ? (
          <form
            className="membership-form"
            data-testid="membership-step-health"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              onSaveHealth();
            }}
          >
            <h2>{kind === "minor" ? copy.health.titleMinor : copy.health.titleAdult}</h2>
            <p className="membership-notice">{w.officialHealthPending}</p>
            {healthFields.map((field) => {
              const label = copy.health[field.id] || field.id;
              if (field.type === "checkbox") {
                return (
                  <label key={field.id} className="membership-check">
                    <input
                      type="checkbox"
                      data-testid={`membership-health-${field.id}`}
                      checked={Boolean(health[field.id])}
                      onChange={(e) => setHealth({ ...health, [field.id]: e.target.checked })}
                    />
                    {label}
                  </label>
                );
              }
              if (field.type === "yes_no") {
                return (
                  <Field key={field.id} id={`h-${field.id}`} label={label}>
                    <select
                      id={`h-${field.id}`}
                      data-testid={`membership-health-${field.id}`}
                      value={health[field.id] || ""}
                      onChange={(e) => setHealth({ ...health, [field.id]: e.target.value })}
                    >
                      <option value="">—</option>
                      <option value="yes">{f.yes}</option>
                      <option value="no">{f.no}</option>
                    </select>
                  </Field>
                );
              }
              return (
                <Field key={field.id} id={`h-${field.id}`} label={label}>
                  {field.type === "text" && field.id.includes("Details") ? (
                    <textarea
                      id={`h-${field.id}`}
                      data-testid={`membership-health-${field.id}`}
                      value={health[field.id] || ""}
                      onChange={(e) => setHealth({ ...health, [field.id]: e.target.value })}
                    />
                  ) : (
                    <input
                      id={`h-${field.id}`}
                      data-testid={`membership-health-${field.id}`}
                      value={health[field.id] || ""}
                      onChange={(e) => setHealth({ ...health, [field.id]: e.target.value })}
                    />
                  )}
                </Field>
              );
            })}
            <div className="membership-actions">
              <button type="button" className="btn-secondary" onClick={() => setStep("identity")}>
                {w.back}
              </button>
              <button type="submit" className="btn-primary" data-testid="membership-health-next">
                {w.submitHealth}
              </button>
            </div>
          </form>
        ) : null}

        {step === "consents" ? (
          <form
            className="membership-form"
            data-testid="membership-step-consents"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              onSaveConsents();
            }}
          >
            <h2>{w.stepConsents}</h2>
            {["privacy", "healthAccuracy", "photoOptional", ...(kind === "minor" ? ["parentalAuthority"] : [])].map((key) => (
              <label key={key} className="membership-check">
                <input
                  type="checkbox"
                  data-testid={`membership-consent-${key}`}
                  checked={Boolean(consents[key])}
                  onChange={(e) => setConsents({ ...consents, [key]: e.target.checked })}
                />
                {copy.consents[key]}
              </label>
            ))}
            <div className="membership-actions">
              <button type="button" className="btn-secondary" onClick={() => setStep("health")}>
                {w.back}
              </button>
              <button type="submit" className="btn-primary" data-testid="membership-consents-next">
                {w.submitConsents}
              </button>
            </div>
          </form>
        ) : null}

        {step === "payment" ? (
          <div data-testid="membership-step-payment">
            <h2>{w.stepPayment}</h2>
            <p className="membership-fee" data-testid="membership-fee">
              {w.feeLabel} : {fee}
            </p>
            <p className="membership-notice">{w.sandboxNotice}</p>
            <p>{copy.payment.noPan}</p>
            <fieldset className="membership-form">
              <legend>{copy.payment.method}</legend>
              {["sandbox", "card", "apple_pay", "google_pay", "paypal"].map((m) => (
                <label key={m} className="membership-check">
                  <input
                    type="radio"
                    name="pay-method"
                    data-testid={`membership-pay-method-${m}`}
                    checked={payMethod === m}
                    onChange={() => setPayMethod(m)}
                  />
                  {copy.payment[m]}
                </label>
              ))}
            </fieldset>
            <div className="membership-actions">
              <button type="button" className="btn-secondary" onClick={() => setStep("consents")}>
                {w.back}
              </button>
              <button type="button" className="btn-primary" data-testid="membership-pay-sandbox" onClick={onPay}>
                {w.pay}
              </button>
            </div>
          </div>
        ) : null}

        {step === "done" ? (
          <div data-testid="membership-step-done">
            <p className="membership-success" data-testid="membership-activated-status">
              {w.activated} — {copy.statuses[record?.status] || record?.status}
            </p>
            <p>
              {w.loginEmail} : <strong data-testid="membership-login-email">{loginId}</strong>
            </p>
            <p>
              {w.loginPassword} : <strong data-testid="membership-login-password">{SANDBOX_MEMBER_PASSWORD}</strong>
            </p>
            {kind === "minor" ? <p>{w.parentManages}</p> : null}
            <Link to="/connexion" className="btn-primary" data-testid="membership-go-login">
              {w.goLogin}
            </Link>
          </div>
        ) : null}
      </GsdReadableBlock>
    </PageSection>
  );
}
